import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2,
  Clock,
  MapPin
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deliverMemberNotification } from '@/lib/notificationDelivery';

export default function AdminAgenda() {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    type: '',
    audiences: [],
    responsible: '',
    status: 'Ativo',
    observations: ''
  });

  const queryClient = useQueryClient();

  const parseEventDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).slice(0, 10);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return new Date(dateStr);
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['adminEvents'],
    queryFn: () => base44.entities.Event.list('-date', 100)
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.filter({ is_active: true })
  });

  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries'],
    queryFn: () => base44.entities.Ministry.filter({ is_active: true })
  });

  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const event = await base44.entities.Event.create(data);
      // Send notifications
      await sendNotifications(event);
      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] });
      toast.success('Evento criado e notificações enviadas!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Event.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] });
      toast.success('Evento atualizado!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Event.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEvents'] });
      toast.success('Evento excluído!');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      description: '',
      type: '',
      audiences: [],
      responsible: '',
      status: 'Ativo',
      observations: ''
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const sendNotifications = async (event) => {
    try {
      const targetMembers = getTargetMembers(event.audiences);
      const formattedDate = format(parseEventDate(event.date), "d 'de' MMMM", { locale: ptBR });
      const title = `Novo Evento: ${event.title}`;
      const message = `Foi criado um novo evento para ${formattedDate} às ${event.start_time || 'horário a definir'}.`;

      await deliverMemberNotification({
        recipients: targetMembers.map((member) => ({
          member_id: member.id,
          member_email: member.email,
        })),
        title,
        message,
        type: 'event',
        referenceId: event.id,
      });

      await Promise.allSettled(
        targetMembers.map(async (member) => {
          if (!member.email) return;

          const whatsappMessage = `🔔 *Novo Evento*\n\n*${event.title}*\n📅 ${formattedDate}\n⏰ ${event.start_time || 'Horário a definir'}\n📍 ${event.location || 'Local a definir'}\n\n${event.description || ''}`;
          await base44.integrations.Core.SendEmail({
            to: member.email,
            subject: title,
            body: whatsappMessage
          });
        })
      );
    } catch (error) {
      console.error('Error sending notifications:', error);
    }
  };

  const getTargetMembers = (audiences) => {
    if (!audiences || audiences.length === 0) return [];
    
    let targetMembers = [];
    
    for (const audience of audiences) {
      if (audience.type === 'Todos') {
        return members; // Return all members
      } else if (audience.type === 'Grupo' && audience.id) {
        const group = groups.find((item) => item.id === audience.id);
        const groupMembers = members.filter((member) =>
          member.group_id === audience.id ||
          (Array.isArray(group?.member_ids) && group.member_ids.includes(member.id))
        );
        targetMembers = [...targetMembers, ...groupMembers];
      } else if (audience.type === 'Ministério' && audience.id) {
        const ministryMembers = members.filter(m => 
          m.ministries?.includes(audience.name)
        );
        targetMembers = [...targetMembers, ...ministryMembers];
      }
    }
    
    // Remove duplicates
    return [...new Map(targetMembers.map(m => [m.id, m])).values()];
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      date: event.date || '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      location: event.location || '',
      description: event.description || '',
      type: event.type || '',
      audiences: event.audiences || [],
      responsible: event.responsible || '',
      status: event.status || 'Ativo',
      observations: event.observations || ''
    });
    setShowForm(true);
  };

  const addAudience = (type) => {
    const newAudience = { type, id: '', name: '' };
    setFormData({ ...formData, audiences: [...formData.audiences, newAudience] });
  };

  const updateAudience = (index, field, value) => {
    const updated = [...formData.audiences];
    updated[index][field] = value;
    
    if (field === 'id') {
      if (updated[index].type === 'Grupo') {
        const group = groups.find(g => g.id === value);
        updated[index].name = group?.name || '';
      } else if (updated[index].type === 'Ministério') {
        const ministry = ministries.find(m => m.id === value);
        updated[index].name = ministry?.name || '';
      }
    }
    
    setFormData({ ...formData, audiences: updated });
  };

  const removeAudience = (index) => {
    setFormData({ 
      ...formData, 
      audiences: formData.audiences.filter((_, i) => i !== index) 
    });
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.date) {
      toast.error('Título e data são obrigatórios.');
      return;
    }

    if (formData.audiences.length === 0) {
      toast.error('Selecione pelo menos um público.');
      return;
    }

    if (editingEvent) {
      updateMutation.mutate({ id: editingEvent.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const eventTypeColors = {
    'Culto': 'bg-indigo-100 text-indigo-700',
    'Reunião': 'bg-blue-100 text-blue-700',
    'Vigília': 'bg-purple-100 text-purple-700',
    'Escola Bíblica': 'bg-amber-100 text-amber-700',
    'Conferência': 'bg-emerald-100 text-emerald-700',
    'Encontro': 'bg-rose-100 text-rose-700',
    'Outro': 'bg-gray-100 text-gray-700',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Agenda" 
            subtitle={`${events.length} eventos`}
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {events.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="Nenhum evento cadastrado"
            description="Crie eventos para a agenda."
            actionLabel="Criar Evento"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`bg-white rounded-xl shadow-sm p-4 ${event.status === 'Cancelado' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-indigo-100 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-xs text-indigo-600 font-medium uppercase">
                      {event.date ? format(parseEventDate(event.date), 'MMM', { locale: ptBR }) : '-'}
                    </span>
                    <span className="text-lg font-bold text-indigo-700">
                      {event.date ? parseEventDate(event.date).getDate() : '-'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{event.title}</h3>
                      {event.status === 'Cancelado' && (
                        <Badge variant="destructive">Cancelado</Badge>
                      )}
                    </div>
                    {event.type && (
                      <Badge className={`mt-1 ${eventTypeColors[event.type] || eventTypeColors['Outro']}`}>
                        {event.type}
                      </Badge>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                      {event.start_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {event.start_time}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleEdit(event)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200"
                    onClick={() => {
                      if (confirm('Excluir este evento?')) {
                        deleteMutation.mutate(event.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Event Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Editar Evento' : 'Novo Evento'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Nome do evento"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Culto">Culto</SelectItem>
                    <SelectItem value="Reunião">Reunião</SelectItem>
                    <SelectItem value="Vigília">Vigília</SelectItem>
                    <SelectItem value="Escola Bíblica">Escola Bíblica</SelectItem>
                    <SelectItem value="Conferência">Conferência</SelectItem>
                    <SelectItem value="Encontro">Encontro</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hora início</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Hora fim</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Local</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Onde será o evento"
              />
            </div>

            <div>
              <Label>Responsável</Label>
              <Input
                value={formData.responsible}
                onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                placeholder="Nome do responsável"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detalhes do evento"
                rows={3}
              />
            </div>

            <div>
              <Label>Público(s) *</Label>
              <div className="space-y-3 mt-2">
                {formData.audiences.map((audience, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Select 
                        value={audience.type} 
                        onValueChange={(v) => updateAudience(index, 'type', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Todos">Todos</SelectItem>
                          <SelectItem value="Grupo">Grupo</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {audience.type === 'Grupo' && (
                        <Select 
                          value={audience.id} 
                          onValueChange={(v) => updateAudience(index, 'id', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o grupo" />
                          </SelectTrigger>
                          <SelectContent>
                            {groups.map(g => (
                              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      

                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => removeAudience(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addAudience('Todos')}
                    className="flex-1"
                  >
                    + Todos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addAudience('Grupo')}
                    className="flex-1"
                  >
                    + Grupo
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                placeholder="Observações adicionais"
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingEvent ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
